import { useState } from 'react'
import type { DailyCardOptionDto } from '@lunary/api-client'
import { Modal } from '@lunary/ui-web/modal'
import { toRoman } from '@lunary/app-core/roman'
import { CardFace } from '../../shared/ui/card-face'
import { errorText, useDailyOptions } from './queries'
import styles from './daily-card.module.css'

type Filter = 'all' | 'major' | 'custom'

type Props = {
  open: boolean
  current: string | null
  pending: boolean
  onClose: () => void
  onPick: (option: DailyCardOptionDto) => void
}

export function CardPicker({ open, current, pending, onClose, onPick }: Props) {
  const options = useDailyOptions(open)
  const [filter, setFilter] = useState<Filter>('all')
  const [query, setQuery] = useState('')
  const [chosen, setChosen] = useState<string | null>(current)
  const items = options.data?.items ?? []
  const customCount = items.filter((o) => o.custom).length
  const q = query.trim().toLowerCase()
  const shown = items.filter((o) => (filter === 'all' || (filter === 'custom') === o.custom) && (!q || o.name.toLowerCase().includes(q)))
  const preview = items.find((o) => o.card_id === chosen) ?? null
  const filters: readonly { key: Filter; label: string }[] = [
    { key: 'all', label: `Все · ${items.length}` },
    { key: 'major', label: `Старшие · ${items.length - customCount}` },
    { key: 'custom', label: `Свои · ${customCount}` },
  ]

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Выберите карту дня"
      description="Подставится описание по умолчанию выбранной карты, его можно поправить перед публикацией."
      footer={
        <>
          <button type="button" className="secondary" onClick={onClose}>
            Отмена
          </button>
          <button type="button" disabled={!preview || pending || preview.card_id === current} onClick={() => preview && onPick(preview)}>
            {pending ? 'Выбираю…' : preview ? `Выбрать «${preview.name}»` : 'Выберите карту'}
          </button>
        </>
      }
    >
      <div className={styles.pickerBar}>
        <div className={styles.seg} role="group" aria-label="Фильтр карт">
          {filters.map((f) => (
            <button key={f.key} type="button" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
        <input type="search" className={styles.search} value={query} placeholder="Найти карту по названию" aria-label="Поиск карты" onChange={(e) => setQuery(e.target.value)} />
      </div>
      {options.isPending && <div className="spinner" aria-label="Загрузка" />}
      {options.isError && <p className="error-box">{errorText(options.error)}</p>}
      <div className={styles.pickerBody}>
        <div className={styles.tiles} role="listbox" aria-label="Карты колоды">
          {shown.map((o) => (
            <button
              key={o.card_id}
              type="button"
              role="option"
              aria-selected={o.card_id === chosen}
              className={styles.tile}
              onClick={() => setChosen(o.card_id)}
              onDoubleClick={() => o.card_id !== current && onPick(o)}
            >
              <span className={styles.tileImg}>
                {o.thumb_url ? <img src={o.thumb_url} alt="" loading="lazy" draggable={false} /> : o.number !== null ? toRoman(o.number) : o.name.charAt(0)}
                {o.custom && <span className={styles.own}>своя</span>}
              </span>
              <span className={styles.tileName}>{o.name}</span>
            </button>
          ))}
          {options.isSuccess && shown.length === 0 && <p className={styles.hint}>Ничего не нашлось.</p>}
        </div>
        {preview && (
          <aside className={styles.preview} aria-live="polite">
            <CardFace src={preview.face_url ?? preview.thumb_url} number={preview.number} name={preview.name} size="md" />
            <b>{preview.name}</b>
            <span className={styles.hint}>{preview.custom ? 'своя карта' : `старший аркан ${toRoman(preview.number ?? 0)}`}</span>
            {preview.template.headline && <span className={styles.previewHead}>{preview.template.headline}</span>}
            <p className={styles.previewBody}>{preview.template.body || 'Описания по умолчанию нет, текст напишете сами.'}</p>
          </aside>
        )}
      </div>
    </Modal>
  )
}
